import { addDays, format, isSameDay, parseISO, startOfDay } from 'date-fns'
import { es } from 'date-fns/locale'
import { formatHumanDate } from '@/shared/utils/format-date'

/**
 * Presentación pura de la pestaña «Actividad»: familia (ícono), actor, línea de detalle y
 * agrupación por día. Sin React ni red, para poder probarla con datos a mano.
 *
 * Los tipos son estructurales y NO importan el SDK: el componente le pasa los eventos del
 * wire y esto sólo lee los campos que necesita.
 */

export type ActivityEvent = {
  action: string
  at: string
  actor: { id: string | null; fullName: string } | null
  actorKind: 'client' | 'staff' | 'system'
  details?: Record<string, string | number | boolean>
}

/** Una familia = un ícono. Agrupa por «de qué trata», no por quién lo hizo. */
export const ACTIVITY_FAMILY = {
  account: 'account',
  invitation: 'invitation',
  signIn: 'sign-in',
  legal: 'legal',
  security: 'security',
  access: 'access',
  other: 'other'
} as const
export type ActivityFamily = (typeof ACTIVITY_FAMILY)[keyof typeof ACTIVITY_FAMILY]

const FAMILY_BY_ACTION: Record<string, ActivityFamily> = {
  'client_account.created': ACTIVITY_FAMILY.account,
  'client_account.email_changed': ACTIVITY_FAMILY.account,
  'client_account.first_run_reset': ACTIVITY_FAMILY.account,
  'client_account.invited': ACTIVITY_FAMILY.invitation,
  'client_account.first_access': ACTIVITY_FAMILY.signIn,
  'client_account.legal_accepted': ACTIVITY_FAMILY.legal,
  'client_account.blocked': ACTIVITY_FAMILY.security,
  'client_account.reactivated': ACTIVITY_FAMILY.security,
  'client_account.sessions_revoked': ACTIVITY_FAMILY.security
}

export function activityFamilyOf(action: string): ActivityFamily {
  const known = FAMILY_BY_ACTION[action]
  if (known) return known
  // Todo `client_grant.*` es del ciclo de vida de un acceso, también los que aún no existen.
  return action.startsWith('client_grant.') ? ACTIVITY_FAMILY.access : ACTIVITY_FAMILY.other
}

/** Quién lo hizo: «Cliente», «Personal IDC · Ana» o «Sistema». */
export function activityActorLabel(event: Pick<ActivityEvent, 'actorKind' | 'actor'>): string {
  if (event.actorKind === 'client') return 'Cliente'
  if (event.actorKind === 'staff') {
    const name = event.actor?.fullName?.trim()
    return name ? `Personal IDC · ${name}` : 'Personal IDC'
  }
  return 'Sistema'
}

const PLATFORM_LABELS: Record<string, string> = { ios: 'iOS', android: 'Android' }

const VIA_LABELS: Record<string, string> = {
  'admin-resend': 'Reenviada desde el panel',
  'negotiation-release': 'Al liberar el acceso desde la negociación'
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined
const num = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

/**
 * La línea tenue bajo el título, armada SÓLO con lo que el servidor publicó en `details`.
 * Devuelve `null` cuando no hay nada que decir: un renglón vacío es ruido.
 */
export function activityDetailLine(
  action: string,
  details: ActivityEvent['details']
): string | null {
  if (!details) return null
  const parts: Array<string | undefined> = []
  switch (action) {
    case 'client_account.legal_accepted': {
      const version = str(details.version)
      const platform = str(details.platform)
      const appVersion = str(details.appVersion)
      parts.push(
        version ? `Versión ${version}` : undefined,
        platform ? (PLATFORM_LABELS[platform] ?? platform) : undefined,
        appVersion ? `app ${appVersion}` : undefined
      )
      break
    }
    case 'client_account.email_changed': {
      const from = str(details.previousEmail)
      const to = str(details.newEmail)
      parts.push(from && to ? `${from} → ${to}` : (to ?? from))
      break
    }
    case 'client_account.first_run_reset': {
      const removed = num(details.acceptancesRemoved)
      parts.push(
        removed === undefined
          ? undefined
          : plural(removed, 'aceptación borrada', 'aceptaciones borradas')
      )
      break
    }
    case 'client_account.sessions_revoked':
      parts.push(
        details.scope === 'all'
          ? 'Todas las sesiones'
          : details.scope === 'one'
            ? 'Una sesión'
            : undefined
      )
      break
    case 'client_account.invited':
    case 'client_grant.created': {
      const via = str(details.via)
      const expiresAt = str(details.expiresAt)
      parts.push(
        via ? (VIA_LABELS[via] ?? undefined) : undefined,
        expiresAt ? `Vence el ${formatHumanDate(expiresAt)}` : undefined
      )
      break
    }
    case 'client_grant.revoked_negotiation_rescinded':
    case 'client_grant.revoked_negotiation_paused':
    case 'client_grant.revoked_inactivity': {
      const revoked = num(details.revoked)
      const days = num(details.inactivityDays)
      parts.push(
        revoked === undefined ? undefined : plural(revoked, 'acceso revocado', 'accesos revocados'),
        days === undefined ? undefined : `Tras ${plural(days, 'día', 'días')} sin actividad`
      )
      break
    }
    case 'client_grant.restored_negotiation_reactivated': {
      const restored = num(details.restored)
      parts.push(
        restored === undefined
          ? undefined
          : plural(restored, 'acceso restituido', 'accesos restituidos')
      )
      break
    }
    case 'client_grant.expiry_scheduled_delivery': {
      const graceDays = num(details.graceDays)
      parts.push(
        graceDays === undefined ? undefined : `Vence en ${plural(graceDays, 'día', 'días')}`
      )
      break
    }
    default:
      return null
  }
  const line = parts.filter(Boolean).join(' · ')
  return line || null
}

/** «Hoy», «Ayer» o «14 de febrero, 2026». */
export function activityDayHeading(day: Date, now: Date): string {
  if (isSameDay(day, now)) return 'Hoy'
  if (isSameDay(day, addDays(startOfDay(now), -1))) return 'Ayer'
  return format(day, "d 'de' MMMM, yyyy", { locale: es })
}

export type ActivityDayGroup<T extends Pick<ActivityEvent, 'at'>> = {
  /** `yyyy-MM-dd` en hora local: sirve de `key` y de ancla para las pruebas. */
  key: string
  heading: string
  events: T[]
}

const UNDATED_KEY = 'undated'
const UNDATED_HEADING = 'Sin fecha'

/**
 * Agrupa por día LOCAL conservando el orden recibido (más nuevo primero). Los días vienen
 * consecutivos porque la entrada ya está ordenada, así que alcanza con mirar el último grupo;
 * eso además hace que una página nueva que continúe el mismo día se una al grupo anterior.
 */
export function groupEventsByDay<T extends Pick<ActivityEvent, 'at'>>(
  events: T[],
  now: Date = new Date()
): Array<ActivityDayGroup<T>> {
  const groups: Array<ActivityDayGroup<T>> = []
  let undated: ActivityDayGroup<T> | undefined
  for (const event of events) {
    const day = parseISO(event.at)
    const valid = !Number.isNaN(day.getTime())
    const key = valid ? format(day, 'yyyy-MM-dd') : UNDATED_KEY
    // Un `at` ilegible NO se descarta —la bitácora mentiría por omisión—: va a su grupo aparte.
    // Los de fecha ilegible se juntan en UN solo grupo, que va al final.
    const target = key === UNDATED_KEY ? undated : groups[groups.length - 1]
    if (target && target.key === key) {
      target.events.push(event)
    } else if (valid) {
      groups.push({ key, heading: activityDayHeading(day, now), events: [event] })
    } else {
      undated = { key, heading: UNDATED_HEADING, events: [event] }
    }
  }
  return undated ? [...groups, undated] : groups
}

/** La hora del renglón, «14:05», en hora local. */
export function activityTime(at: string): string {
  const date = parseISO(at)
  return Number.isNaN(date.getTime()) ? '' : format(date, 'HH:mm')
}
